/**
 * Theme Configuration - Light Theme Implementation
 * Based on Design Specification for Map-Focused Dashboard
 * - Clean light theme with blue primary palette
 * - WCAG 2.1 AA compliant contrast ratios
 * - D3-powered heatmap color scales for ticket count visualization
 * - Optimized for map-focused UI design
 */

import * as d3 from 'd3';

// Light Theme Color System - Based on Design Specification Section 4
const colors = {
  // Primary Colors - Clean Blues (#0ea5e9 as main)
  primary: {
    50: '#f0f9ff',
    100: '#e0f2fe',
    200: '#bae6fd',
    300: '#7dd3fc',
    400: '#38bdf8',
    500: '#0ea5e9', // Main primary - Clean blue
    600: '#0284c7',
    700: '#0369a1',
    800: '#075985',
    900: '#0c4a6e'
  },
  
  // Secondary colors (keeping compatible secondary)
  secondary: {
    50: '#f0f9ff',
    100: '#e0f2fe',
    200: '#bae6fd',
    300: '#7dd3fc',
    400: '#38bdf8',
    500: '#0ea5e9',
    600: '#0284c7',
    700: '#0369a1',
    800: '#075985',
    900: '#0c4a6e'
  },
  
  // Neutral Colors - Light Theme optimized
  neutral: {
    50: '#fafafa',
    100: '#f4f4f5',
    200: '#e4e4e7',
    300: '#d4d4d8',
    400: '#a1a1aa',
    500: '#71717a',
    600: '#52525b',
    700: '#3f3f46',
    800: '#27272a',
    900: '#18181b'
  },
  
  // Semantic Status Colors
  success: '#10b981',
  warning: '#f59e0b',
  error: '#ef4444',
  info: '#3b82f6',
  
  // Enhanced Multicolor Heatmap - 7-Step Color Scale (Design Spec Section 6)
  // Progresses from light gray through blue, teal, orange to red for better visual distinction
  heatmap: {
    0: '#f8fafc',  // No data - Very light gray
    1: '#e0f2fe',  // Very low (1-10) - Light blue
    2: '#7dd3fc',  // Low (11-25) - Medium blue
    3: '#06b6d4',  // Low-Medium (26-50) - Teal/cyan
    4: '#f59e0b',  // Medium (51-75) - Orange
    5: '#f97316',  // Medium-High (76-100) - Dark orange
    6: '#ef4444',  // High (101-150) - Red
    7: '#dc2626'   // Very High (150+) - Dark red
  },
  
  // Alternative colorblind-friendly heatmap (for accessibility)
  heatmapColorblind: {
    0: '#f8fafc',  // No data - Very light gray
    1: '#ddd6fe',  // Very low - Light purple
    2: '#a78bfa',  // Low - Medium purple
    3: '#7c3aed',  // Low-Medium - Dark purple
    4: '#fbbf24',  // Medium - Yellow
    5: '#f59e0b',  // Medium-High - Orange
    6: '#ea580c',  // High - Dark orange
    7: '#c2410c'   // Very High - Very dark orange
  },
  
  // Map-specific colors for enhanced visualization
  map: {
    noData: '#cbd5e1', // Darker gray for better contrast
    hover: '#fbbf24',
    selected: '#f97316',
    border: '#e4e4e7',
    background: '#ffffff'
  }
};

// TODO: Configure typography system
const typography = {
  fontFamily: {
    primary: '"Roboto", "Helvetica", "Arial", sans-serif',
    secondary: '"Roboto Condensed", "Arial Narrow", sans-serif',
    monospace: '"Roboto Mono", "Courier New", monospace'
  },
  
  fontSize: {
    xs: '0.75rem',    // 12px
    sm: '0.875rem',   // 14px
    base: '1rem',     // 16px
    lg: '1.125rem',   // 18px
    xl: '1.25rem',    // 20px
    '2xl': '1.5rem',  // 24px
    '3xl': '1.875rem', // 30px
    '4xl': '2.25rem', // 36px
    '5xl': '3rem',    // 48px
    '6xl': '4rem'     // 64px
  },
  
  fontWeight: {
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700
  },
  
  lineHeight: {
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.75
  }
};

// TODO: Define spacing scale
const spacing = {
  0: '0',
  1: '0.25rem',   // 4px
  2: '0.5rem',    // 8px
  3: '0.75rem',   // 12px
  4: '1rem',      // 16px
  5: '1.25rem',   // 20px
  6: '1.5rem',    // 24px
  8: '2rem',      // 32px
  10: '2.5rem',   // 40px
  12: '3rem',     // 48px
  16: '4rem',     // 64px
  20: '5rem',     // 80px
  24: '6rem'      // 96px
};

// Light Theme Shadow System (Design Spec Section 2.3)
const shadows = {
  xs: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  sm: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
};

// TODO: Define border radius values
const borderRadius = {
  none: '0',
  sm: '0.125rem',   // 2px
  base: '0.25rem',  // 4px
  md: '0.375rem',   // 6px
  lg: '0.5rem',     // 8px
  xl: '0.75rem',    // 12px
  '2xl': '1rem',    // 16px
  full: '9999px'
};

// TODO: Configure breakpoints for responsive design
const breakpoints = {
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  '2xl': '1536px'
};

// Light Theme Configuration - Map-Focused Design
const lightTheme = {
  colors: {
    ...colors,
    // Background Colors (Design Spec Section 2.1)
    background: {
      primary: '#ffffff',      // --bg-primary
      secondary: '#fafafa',    // --bg-secondary
      tertiary: '#f4f4f5',    // --bg-tertiary
      card: '#ffffff',         // --bg-card
      overlay: 'rgba(255, 255, 255, 0.95)', // --bg-overlay
      topbar: '#0d3475',       // --bg-topbar - Dark blue for header
      kpiContainer: 'transparent'  // --bg-kpi-container - Transparent for smooth transition
    },
    // Text Colors with WCAG 2.1 AA compliance
    text: {
      primary: '#18181b',      // --text-primary
      secondary: '#52525b',    // --text-secondary
      muted: '#71717a',        // --text-muted
      inverse: '#ffffff'       // --text-inverse
    },
    // Border Colors
    border: {
      light: '#e4e4e7',        // --border-light
      medium: '#d4d4d8',       // --border-medium
      strong: '#a1a1aa'        // --border-strong
    },
    // Semantic Colors with light variants
    semantic: {
      success: '#10b981',
      successLight: '#d1fae5',
      warning: '#f59e0b',
      warningLight: '#fef3c7',
      error: '#ef4444',
      errorLight: '#fee2e2',
      info: '#3b82f6',
      infoLight: '#dbeafe'
    }
  },
  typography,
  spacing,
  shadows,
  borderRadius,
  breakpoints
};

// Dark theme configuration (keeping for compatibility)
const darkTheme = {
  colors: {
    ...colors,
    background: {
      primary: colors.neutral[900],
      secondary: colors.neutral[800],
      card: colors.neutral[800]
    },
    text: {
      primary: colors.neutral[50],
      secondary: colors.neutral[200],
      muted: colors.neutral[400]
    }
  },
  typography,
  spacing,
  shadows,
  borderRadius,
  breakpoints
};

// D3-powered heatmap color scales with custom gradient
const createD3ColorScale = (useColorblindFriendly = false) => {
  if (useColorblindFriendly) {
    const heatmapColors = colors.heatmapColorblind;
    const scaleColors = [];
    for (let i = 1; i <= 7; i++) {
      scaleColors.push(heatmapColors[i]);
    }
    return d3.scaleSequential()
      .interpolator(d3.interpolateRgbBasis(scaleColors));
  }
  
  // Use the requested custom gradient: light blue to dark blue
  return d3.scaleLinear()
    .range(["#cadafe", "#0d3475"]);
};

// Alternative: Create the original multi-color scale
const createD3MultiColorScale = (useColorblindFriendly = false) => {
  const heatmapColors = useColorblindFriendly ? colors.heatmapColorblind : colors.heatmap;
  
  // Create an array of colors excluding the "no data" color (index 0)
  const scaleColors = [];
  for (let i = 1; i <= 7; i++) {
    scaleColors.push(heatmapColors[i]);
  }
  
  // Create D3 sequential color scale with smooth interpolation
  return d3.scaleSequential()
    .interpolator(d3.interpolateRgbBasis(scaleColors));
};

// Create quantile-based color scale for better data distribution
const createD3QuantileScale = (data, useColorblindFriendly = false) => {
  if (!data || data.length === 0) return null;
  
  const values = data.map(d => d.open_count || 0).filter(v => v > 0);
  if (values.length === 0) return null;
  
  const heatmapColors = useColorblindFriendly ? colors.heatmapColorblind : colors.heatmap;
  const scaleColors = [];
  for (let i = 1; i <= 7; i++) {
    scaleColors.push(heatmapColors[i]);
  }
  
  return d3.scaleQuantile()
    .domain(values)
    .range(scaleColors);
};

// Create threshold-based color scale for precise control
const createD3ThresholdScale = (ranges, useColorblindFriendly = false) => {
  if (!ranges || ranges.length === 0) return null;
  
  const heatmapColors = useColorblindFriendly ? colors.heatmapColorblind : colors.heatmap;
  const thresholds = ranges.slice(1).map(r => r.min); // Skip first range, use min values as thresholds
  const scaleColors = ranges.map(r => heatmapColors[r.colorIndex]);
  
  return d3.scaleThreshold()
    .domain(thresholds)
    .range(scaleColors);
};

// Enhanced color calculation using D3 scales
const calculateHeatmapColor = (value, minValue, maxValue, useColorblindFriendly = false) => {
  if (value === 0 || value === null || value === undefined) {
    return useColorblindFriendly ? colors.heatmapColorblind[0] : colors.heatmap[0];
  }
  
  // Avoid division by zero
  if (maxValue === minValue) {
    return useColorblindFriendly ? colors.heatmapColorblind[1] : "#cadafe"; // Light blue for single values
  }
  
  // Create D3 color scale and apply it
  const colorScale = createD3ColorScale(useColorblindFriendly);
  colorScale.domain([minValue, maxValue]);
  
  return colorScale(value);
};

// Calculate dynamic ranges for better color distribution
const calculateDynamicRanges = (data, steps = 7) => {
  if (!data || data.length === 0) return [];
  
  const values = data.map(d => d.open_count || 0).filter(v => v > 0).sort((a, b) => a - b);
  if (values.length === 0) return [];
  
  const ranges = [];
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  
  // If all values are the same, create a single range
  if (minValue === maxValue) {
    return [{
      min: minValue,
      max: maxValue,
      colorIndex: 4, // Use middle color
      label: 'All Values'
    }];
  }
  
  // Create evenly distributed ranges based on actual data
  const step = (maxValue - minValue) / (steps - 1);
  
  for (let i = 0; i < steps; i++) {
    const rangeMin = i === 0 ? minValue : Math.ceil(minValue + (step * i));
    const rangeMax = i === steps - 1 ? maxValue : Math.floor(minValue + (step * (i + 1)));
    
    ranges.push({
      min: rangeMin,
      max: rangeMax,
      colorIndex: i + 1,
      label: i === 0 ? 'Very Low' :
             i === 1 ? 'Low' :
             i === 2 ? 'Low-Medium' :
             i === 3 ? 'Medium' :
             i === 4 ? 'Medium-High' :
             i === 5 ? 'High' : 'Very High'
    });
  }
  
  return ranges;
};

// Get color for value using dynamic ranges with D3 threshold scale
const getColorFromDynamicRange = (value, ranges, useColorblindFriendly = false) => {
  if (value === 0 || value === null || value === undefined) {
    return useColorblindFriendly ? colors.heatmapColorblind[0] : colors.heatmap[0];
  }
  
  if (!ranges || ranges.length === 0) {
    return useColorblindFriendly ? colors.heatmapColorblind[1] : colors.heatmap[1];
  }
  
  // Use D3 threshold scale for better performance and accuracy
  const thresholdScale = createD3ThresholdScale(ranges, useColorblindFriendly);
  if (!thresholdScale) {
    return useColorblindFriendly ? colors.heatmapColorblind[1] : colors.heatmap[1];
  }
  
  return thresholdScale(value);
};

// Alternative: Get color using D3 quantile scale (better for uneven data distribution)
const getColorFromQuantileScale = (value, data, useColorblindFriendly = false) => {
  if (value === 0 || value === null || value === undefined) {
    return useColorblindFriendly ? colors.heatmapColorblind[0] : colors.heatmap[0];
  }
  
  const quantileScale = createD3QuantileScale(data, useColorblindFriendly);
  if (!quantileScale) {
    return useColorblindFriendly ? colors.heatmapColorblind[1] : colors.heatmap[1];
  }
  
  return quantileScale(value);
};

// Create gradient string for legends using D3 interpolation
const createHeatmapGradient = (useColorblindFriendly = false) => {
  if (useColorblindFriendly) {
    const heatmapColors = colors.heatmapColorblind;
    const gradientColors = [];
    
    for (let i = 1; i <= 7; i++) {
      gradientColors.push(heatmapColors[i]);
    }
    
    // Use D3 to create smooth interpolated gradient with more steps for better visual quality
    const interpolator = d3.interpolateRgbBasis(gradientColors);
    const smoothGradientColors = [];
    
    // Generate 20 intermediate colors for smoother gradient
    for (let i = 0; i <= 20; i++) {
      const t = i / 20;
      smoothGradientColors.push(interpolator(t));
    }
    
    return `linear-gradient(to right, ${smoothGradientColors.join(', ')})`;
  }
  
  // Use the custom gradient: light blue to dark blue
  const interpolator = d3.interpolateRgb("#cadafe", "#0d3475");
  const smoothGradientColors = [];
  
  // Generate 20 intermediate colors for smoother gradient
  for (let i = 0; i <= 20; i++) {
    const t = i / 20;
    smoothGradientColors.push(interpolator(t));
  }
  
  return `linear-gradient(to right, ${smoothGradientColors.join(', ')})`;
};

// Create D3-powered color interpolator for smooth transitions
const createColorInterpolator = (useColorblindFriendly = false) => {
  const heatmapColors = useColorblindFriendly ? colors.heatmapColorblind : colors.heatmap;
  const gradientColors = [];
  
  for (let i = 1; i <= 7; i++) {
    gradientColors.push(heatmapColors[i]);
  }
  
  return d3.interpolateRgbBasis(gradientColors);
};

// Three distinct monochromatic gradients
const createGreenGradient = () => {
  return d3.scaleLinear()
    .range(['#dcfce7', '#166534']); // Light green to dark green
};

const createYellowGradient = () => {
  return d3.scaleLinear()
    .range(['#fff7ed', '#ea580c']); // Light yellow-orange to dark orange
};

const createRedGradient = () => {
  return d3.scaleLinear()
    .range(['#fef2f2', '#b91c1c']); // Light red to less saturated dark red
};

// Tiered bubble color system - divides data into thirds and applies appropriate gradient
const createTieredBubbleColorScale = (data) => {
  if (!data || data.length === 0) return null;
  
  const values = data.map(d => d.open_count || 0).filter(v => v > 0).sort((a, b) => a - b);
  if (values.length === 0) return null;
  
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const range = maxValue - minValue;
  
  // Divide into thirds
  const lowerThird = minValue + (range / 3);
  const upperThird = minValue + (2 * range / 3);
  
  return {
    getColor: (value) => {
      if (value <= lowerThird) {
        // Lower third: use green gradient
        const greenScale = createGreenGradient();
        greenScale.domain([minValue, lowerThird]);
        return greenScale(value);
      } else if (value <= upperThird) {
        // Middle third: use yellow gradient
        const yellowScale = createYellowGradient();
        yellowScale.domain([lowerThird, upperThird]);
        return yellowScale(value);
      } else {
        // Upper third: use red gradient
        const redScale = createRedGradient();
        redScale.domain([upperThird, maxValue]);
        return redScale(value);
      }
    },
    thresholds: { lowerThird, upperThird },
    range: { minValue, maxValue }
  };
};

// Create CSS gradient string for tiered bubble legends
const createTieredBubbleGradient = () => {
  // Create three separate gradients and combine them
  const greenColors = ['#dcfce7', '#22c55e', '#166534'];
  const yellowColors = ['#fff7ed', '#f97316', '#ea580c'];
  const redColors = ['#fef2f2', '#ef4444', '#b91c1c'];
  
  // Use the middle color of each gradient for the combined legend
  const combinedColors = [greenColors[1], yellowColors[1], redColors[1]];
  
  // Use D3 to create smooth interpolated gradient
  const interpolator = d3.interpolateRgbBasis(combinedColors);
  const smoothGradientColors = [];
  
  // Generate 20 intermediate colors for smoother gradient
  for (let i = 0; i <= 20; i++) {
    const t = i / 20;
    smoothGradientColors.push(interpolator(t));
  }
  
  return `linear-gradient(to right, ${smoothGradientColors.join(', ')})`;
};

// Export theme configurations and utilities
export {
  lightTheme,
  darkTheme,
  colors,
  typography,
  spacing,
  shadows,
  borderRadius,
  breakpoints,
  calculateHeatmapColor,
  calculateDynamicRanges,
  getColorFromDynamicRange,
  getColorFromQuantileScale,
  createHeatmapGradient,
  createColorInterpolator,
  createD3ColorScale,
  createD3MultiColorScale,
  createD3QuantileScale,
  createD3ThresholdScale,
  createGreenGradient,
  createYellowGradient,
  createRedGradient,
  createTieredBubbleColorScale,
  createTieredBubbleGradient
};

// Default export (light theme as default for new design)
export default lightTheme;